import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { EMPTY, Observable, Subject, Subscription, filter, map, tap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';

import { finShare } from '@fcom/rx';
import { DISCOUNT_CODE_PATTERN, isNotBlank, isPresent, unsubscribe } from '@fcom/utils';
import {
  ButtonSize,
  ButtonTheme,
  LoaderTheme,
  ModalButtons,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { FormStatus } from '@fcom/service-forms';
import { CmsDataService, PromoCodeTerms } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { BookingAppState } from '@fcom/booking/interfaces';
import { GlobalBookingActions, globalBookingDiscountCode } from '@fcom/common/store';

import { DiscountCodeRulesState } from '../../interfaces';
import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';

@Component({
  selector: 'fin-discount-code',
  templateUrl: './discount-code.component.html',
  styleUrls: ['./discount-code.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiscountCodeComponent implements OnInit, OnDestroy {
  readonly FormStatus = FormStatus;
  readonly ModalButtons = ModalButtons;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationLayout = NotificationLayout;
  readonly LoaderTheme = LoaderTheme;

  modalOpen = false;
  discountCode$: Observable<string>;
  discountCodeForm: FormGroup<{ discountCode: FormControl<string> }>;
  discountCodeRules$: Observable<DiscountCodeRulesState>;
  loading$: Subject<boolean> = new Subject<boolean>();
  openModal$: Subject<void> = new Subject<void>();
  private subscriptions: Subscription = new Subscription();

  constructor(
    private cmsDataService: CmsDataService,
    private languageService: LanguageService,
    private bookingWidgetGtmService: BookingWidgetGtmService,
    private store$: Store<BookingAppState>
  ) {}

  ngOnInit(): void {
    this.discountCode$ = this.store$.pipe(
      globalBookingDiscountCode(),
      map((discountCode: string) => (isPresent(discountCode) ? discountCode : null)),
      finShare()
    );

    this.discountCodeForm = new FormGroup({
      discountCode: new FormControl('', Validators.pattern(DISCOUNT_CODE_PATTERN)),
    });

    this.subscriptions.add(
      this.discountCodeForm.controls.discountCode.valueChanges
        .pipe(filter(Boolean), finShare())
        .subscribe((code: string) => {
          this.discountCodeForm.controls.discountCode.patchValue(code.toUpperCase(), { emitEvent: false });
          if (
            !this.discountCodeForm.controls.discountCode.valid &&
            !this.discountCodeForm.controls.discountCode.touched
          ) {
            this.discountCodeForm.controls.discountCode.markAsTouched();
          }
        })
    );

    this.subscriptions.add(
      this.openModal$.pipe(withLatestFrom(this.discountCode$)).subscribe(([_openModal, discountCode]) => {
        if (!(isPresent(discountCode) && isNotBlank(discountCode))) {
          this.resetForm();
        }
        this.modalOpen = true;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  checkDiscountCode(): void {
    if (
      !this.discountCodeForm.valid ||
      this.discountCodeForm.controls.discountCode.value === null ||
      this.discountCodeForm.controls.discountCode.value === ''
    ) {
      return;
    }
    this.loading$.next(true);

    this.discountCodeRules$ = this.cmsDataService
      .getPromoCodeTerms(
        this.discountCodeForm.controls.discountCode.value,
        this.languageService.langValue,
        this.languageService.localeValue
      )
      .pipe(
        map((terms: PromoCodeTerms) => ({
          found: !!terms,
          terms: terms?.description,
          title: terms?.title,
        })),
        tap((data: DiscountCodeRulesState) => {
          this.loading$.next(false);
          if (!data.found) {
            this.discountCodeForm.controls.discountCode.setErrors({ custom: 'notFoundError' });
          } else {
            this.discountCodeForm.controls.discountCode.disable();
            this.discountCodeForm.controls.discountCode.setErrors(null);
          }
        }),
        finShare()
      );
  }

  resetForm(): void {
    this.discountCodeForm.reset();
    this.discountCodeRules$ = EMPTY;
    this.discountCodeForm.controls.discountCode.enable();
  }

  removeCode(): void {
    this.store$.dispatch(GlobalBookingActions.setDiscountCode({ discountCode: null }));
    this.resetForm();
  }

  continue(): void {
    this.store$.dispatch(
      GlobalBookingActions.setDiscountCode({ discountCode: this.discountCodeForm.controls.discountCode.value })
    );
    this.bookingWidgetGtmService.trackElementEvent(
      'promo-code-modal-done',
      this.discountCodeForm.controls.discountCode.value
    );
    this.modalOpen = false;
  }
}
